/**
 * Maintenance Ticket Tag mutations using React Relay
 *
 * Note: Run `npm run relay` after making changes to GraphQL mutations
 * to regenerate the type artifacts.
 */

import { useCallback } from 'react';
import { useMutation, commitMutation } from 'react-relay';
// import graphql from 'babel-plugin-relay/macro'; // temporarily disabled
import { relayEnvironment } from '../../../../relay';
import type { useMaintenanceTagMutationsCreateMutation } from './__generated__/useMaintenanceTagMutationsCreateMutation.graphql';
import type { useMaintenanceTagMutationsUpdateMutation } from './__generated__/useMaintenanceTagMutationsUpdateMutation.graphql';

// ============================================================================
// GraphQL Mutations
// ============================================================================

const CreateMaintenanceTicketTagMutation = null as any /* graphql mutation temporarily disabled - mutations removed from schema */;

const UpdateMaintenanceTicketTagMutation = null as any /* graphql mutation temporarily disabled - mutations removed from schema */;

// ============================================================================
// Types
// ============================================================================

export interface MutationError {
  code?: string | null;
  fields?: readonly string[] | null;
  message?: string | null;
  shortMessage?: string | null;
  vars?: unknown;
}

export interface MutationResult<T> {
  data: T | null;
  errors: MutationError[];
  isSuccess: boolean;
}

export interface CreateMaintenanceTicketTagInput {
  name: string;
  systemId?: string;
  partnerId?: string;
}

export interface UpdateMaintenanceTicketTagInput {
  name?: string;
  archivedOn?: string;
}

// ============================================================================
// Hooks
// ============================================================================

export function useCreateMaintenanceTagMutation() {
  const [commit, isInFlight] =
    useMutation<useMaintenanceTagMutationsCreateMutation>(
      CreateMaintenanceTicketTagMutation
    );

  const createMaintenanceTag = useCallback(
    (
      input: CreateMaintenanceTicketTagInput
    ): Promise<MutationResult<any>> => {
      return new Promise((resolve) => {
        commit({
          variables: { input },
          onCompleted: (response) => {
            const result = response.createMaintenanceTicketTag;
            resolve({
              data: result.result ?? null,
              errors: result.errors as MutationError[],
              isSuccess:
                result.errors.length === 0 && result.result !== null,
            });
          },
          onError: (error) => {
            resolve({
              data: null,
              errors: [{ message: error.message }],
              isSuccess: false,
            });
          },
        });
      });
    },
    [commit]
  );

  return { createMaintenanceTag, isLoading: isInFlight };
}

export function useUpdateMaintenanceTagMutation() {
  const [commit, isInFlight] =
    useMutation<useMaintenanceTagMutationsUpdateMutation>(
      UpdateMaintenanceTicketTagMutation
    );

  const updateMaintenanceTag = useCallback(
    (
      tagId: string,
      input: UpdateMaintenanceTicketTagInput
    ): Promise<MutationResult<any>> => {
      return new Promise((resolve) => {
        commit({
          variables: { id: tagId, input },
          onCompleted: (response) => {
            const result = response.updateMaintenanceTicketTag;
            resolve({
              data: result.result ?? null,
              errors: result.errors as MutationError[],
              isSuccess:
                result.errors.length === 0 && result.result !== null,
            });
          },
          onError: (error) => {
            resolve({
              data: null,
              errors: [{ message: error.message }],
              isSuccess: false,
            });
          },
        });
      });
    },
    [commit]
  );

  return { updateMaintenanceTag, isLoading: isInFlight };
}

// ============================================================================
// Imperative API (without hooks)
// ============================================================================

export function createMaintenanceTagMutation(
  input: CreateMaintenanceTicketTagInput
): Promise<MutationResult<any>> {
  return new Promise((resolve) => {
    commitMutation<useMaintenanceTagMutationsCreateMutation>(
      relayEnvironment,
      {
        mutation: CreateMaintenanceTicketTagMutation,
        variables: { input },
        onCompleted: (response) => {
          const result = response.createMaintenanceTicketTag;
          resolve({
            data: result.result,
            errors: result.errors as MutationError[],
            isSuccess:
              result.errors.length === 0 && result.result !== null,
          });
        },
        onError: (error) => {
          resolve({
            data: null,
            errors: [{ message: error.message }],
            isSuccess: false,
          });
        },
      }
    );
  });
}

export function updateMaintenanceTagMutation(
  tagId: string,
  input: UpdateMaintenanceTicketTagInput
): Promise<MutationResult<any>> {
  return new Promise((resolve) => {
    commitMutation<useMaintenanceTagMutationsUpdateMutation>(
      relayEnvironment,
      {
        mutation: UpdateMaintenanceTicketTagMutation,
        variables: { id: tagId, input },
        onCompleted: (response) => {
          const result = response.updateMaintenanceTicketTag;
          resolve({
            data: result.result,
            errors: result.errors as MutationError[],
            isSuccess:
              result.errors.length === 0 && result.result !== null,
          });
        },
        onError: (error) => {
          resolve({
            data: null,
            errors: [{ message: error.message }],
            isSuccess: false,
          });
        },
      }
    );
  });
}
